/* eslint-disable jira/import-whitelist */
import { prefetchedRemoteDataManager } from '@jira/issue__fetch-services/src/prefetchedRemoteDataManager.tsx';
import { fetchConfluenceSmartlinkData } from '@jira/issue__fetch-services/src/fetchConfluenceSmartlinkData.tsx';
import { fetchWeblinksSmartlinkData } from '@jira/issue__fetch-services/src/fetchWeblinksSmartlinkData.tsx';
import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';
import type { PrefetchedSmartlinkData } from '@jira/issue__fetch-services/src/fetchORSDataSSR.tsx';
import type { RemoteLinks } from '@jira/issue__shared-types/src/TypesServerRemoteLinks.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';

const getIssueViewRemoteData = async (
	{ match }: RouterDataContext,
	_storeContext: ResourceStoreContext,
): Promise<{
	remoteData: {
		id: string;
		remoteLinks: RemoteLinks;
	};
	smartlinkData: PrefetchedSmartlinkData[] | null;
	weblinksSmartlinkData: PrefetchedSmartlinkData[] | null;
} | null> => {
	const issueKey = getIssueKey(match);

	if (!issueKey) {
		return null;
	}

	// Feature flag to control whether we return data (SSR) or just prefetch (old behavior)

	if (fg('dfo_issue_view_remote_data_srr_group')) {
		// Fetch the remote data and await the result for SSR
		const { tenantContext } = _storeContext;
		const cloudId = tenantContext?.cloudId;
		const promise = prefetchedRemoteDataManager.fetchTransformAndStore(issueKey, cloudId);
		const remoteData = await promise;

		const [smartlinkResult, weblinksSmartlinkResult] = await Promise.allSettled([
			fetchConfluenceSmartlinkData(remoteData, cloudId),
			fetchWeblinksSmartlinkData(remoteData, cloudId),
		]);

		if (smartlinkResult.status === 'rejected') {
			log.safeInfoWithoutCustomerData(
				'issue-view-remote-data.get-data',
				'An error was thrown while prefetching confluence smart link data',
			);
		}

		if (weblinksSmartlinkResult.status === 'rejected') {
			log.safeInfoWithoutCustomerData(
				'issue-view-remote-data.get-data',
				'An error was thrown while prefetching weblinks smart link data',
			);
		}

		const smartlinkData = smartlinkResult.status === 'fulfilled' ? smartlinkResult.value : null;
		const weblinksSmartlinkData =
			weblinksSmartlinkResult.status === 'fulfilled' ? weblinksSmartlinkResult.value : null;

		return { remoteData, smartlinkData, weblinksSmartlinkData };
	}

	// Old behavior: just prefetch, don't return data
	prefetchedRemoteDataManager.prefetch(issueKey);
	return null;
};

export default getIssueViewRemoteData;
